import { createSelector } from "reselect";
import _ from "underscore";
import { planMetalTierToNumber } from "Utils/plansUtils";

import { getPlans } from "Common/PlanLoader/Selectors";
import type { PlanLoaderState } from "Common/PlanLoader/Selectors";

import type { DentalPlan, VisionPlan, MedicalPlanPrice, Cart } from "Utils/types";
// TODO(daniel.sep-7-2022)
// These selectors are coupled to where the plans are placed in the store.
// The CartService's responsibility should only be to update the selected
// plans' ids in the backend.
// The selectors here should only be responsible for returning said ids
// to which ever app is using the CartService.
// So this CartServiceState will have duplicated keys with the PlanLoaderState
// And will end up 'forcing' any app that wants to use the CartService to also
// have these keys in the store.
export type CartServiceState = {
  cart: Cart;
  // The keys below should be defined on a per app basis.
  dentalPlans: Array<DentalPlan>;
  visionPlans: Array<VisionPlan>;
  loadingDentalPlans: boolean;
  loadingVisionPlans: boolean;
};

const getDentalPlans = (state: CartServiceState): Array<DentalPlan> => state.dentalPlans;

const getVisionPlans = (state: CartServiceState): Array<VisionPlan> => state.visionPlans;

export const getCart = (state: CartServiceState): Cart => state.cart;
export const getCartItems: (arg0: CartServiceState) => number = createSelector(
  [getCart],
  (cart: Cart): number => _.filter(_.flatten(Object.values(cart)), (val) => val !== null && !_.isEmpty(_.compact(val))).length,
);
// TODO(daniel.sep-7-2022): This is a concrete example of this coupling.
type CoupledStates = CartServiceState & PlanLoaderState;
export const getSelectedMedicalPlans: (arg0: CoupledStates) => Array<MedicalPlanPrice> = createSelector(
  [getPlans, getCart],
  (medicalPlans: Array<MedicalPlanPrice>, cart: Cart): Array<MedicalPlanPrice> =>
    _.sortBy(
      _.filter(medicalPlans, (plan) => _.find(cart.medical, (id) => id == plan?.id)),
      (p) => planMetalTierToNumber(p),
    ),
);
export const getCartMedicalPlanIds: (arg0: CartServiceState) => Array<string> = createSelector([getCart], (cart: Cart): Array<string> => cart.medical);
export const getSelectedDentalPlan: (arg0: CartServiceState) => DentalPlan | null | undefined = createSelector(
  [getDentalPlans, getCart],
  (dentalPlans: Array<DentalPlan>, cart: Cart): DentalPlan | null | undefined =>
    _.findWhere(dentalPlans, {
      id: cart.dental,
    }),
);
export const getSelectedVisionPlan: (arg0: CartServiceState) => VisionPlan | null | undefined = createSelector(
  [getVisionPlans, getCart],
  (visionPlans: Array<VisionPlan>, cart: Cart): VisionPlan | null | undefined =>
    _.findWhere(visionPlans, {
      id: cart.vision,
    }),
);
export const getSelectedMedicalPlanCarrier: (arg0: CoupledStates) => string | null | undefined = createSelector(
  [getSelectedMedicalPlans],
  (plans: Array<MedicalPlanPrice>): string | null | undefined => (plans[0] ? plans[0].carrier : null),
);
export const isLoadingDentalPlans = (state: CartServiceState): boolean => state.loadingDentalPlans;
export const isLoadingVisionPlans = (state: CartServiceState): boolean => state.loadingVisionPlans;
export const getDentalPlansByCarrier: (arg0: CoupledStates) => Array<DentalPlan> = createSelector(
  [getDentalPlans, getSelectedMedicalPlanCarrier, isLoadingDentalPlans],
  (plans: Array<DentalPlan>, carrier: string | null | undefined, isLoadingPlans: boolean): Array<DentalPlan> => {
    const filteredPlans = _.filter(plans, (plan) => plan.carrier === carrier);

    if (filteredPlans.length > 0) {
      return filteredPlans;
    }

    const uhcPlans = _.filter(plans, (plan) => plan.carrier === "UnitedHealthcare");

    if (uhcPlans.length > 0) {
      return uhcPlans;
    }
    return isLoadingPlans ? [] : plans;
  },
);
export const getRecommendedDentalPlans: (arg0: CartServiceState) => Array<DentalPlan> = createSelector(
  [getDentalPlans, isLoadingDentalPlans],
  (plans: Array<DentalPlan>, isLoadingPlans: boolean): Array<DentalPlan> => (isLoadingPlans ? [] : plans),
);
export const getVisionPlansByCarrier: (arg0: CoupledStates) => Array<VisionPlan> = createSelector(
  [getVisionPlans, getSelectedMedicalPlanCarrier, isLoadingVisionPlans],
  (plans: Array<VisionPlan>, carrier: string | null | undefined, isLoadingPlans: boolean): Array<VisionPlan> => {
    const filteredPlans = _.filter(plans, (plan) => plan.carrier === carrier);

    if (filteredPlans.length > 0) {
      return filteredPlans;
    }

    const uhcPlans = _.filter(plans, (plan) => plan.carrier === "UnitedHealthcare");

    if (uhcPlans.length > 0) {
      return uhcPlans;
    }
    return isLoadingPlans ? [] : plans;
  },
);
